import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StepSection } from '@/components/step-section';
import { TextField } from '@/components/text-field';
import { ApiError } from '@/lib/api';
import { field_errors_from, form_error_message } from '@/lib/forms';
import { use_draft } from '@/features/onboarding/hooks';
import { next_module_step_after } from '@/features/onboarding/module-step-router';
import {
  use_pharmacy_details,
  use_save_pharmacy_details,
} from '@/features/pharmacy/hooks';
import {
  DRUG_LICENSE_MAX,
  DRUG_LICENSE_MIN,
  DRUG_LICENSE_REGEX,
} from '@/features/pharmacy/types';

export function ModulePharmacyPage() {
  const navigate = useNavigate();
  const draft_q = use_draft();
  const saved_q = use_pharmacy_details({ enabled: !!draft_q.data });
  const save_m = use_save_pharmacy_details();
  const [drug_license_number, set_drug_license_number] = useState('');
  const [prefilled, set_prefilled] = useState(false);
  const [error, set_error] = useState<string | null>(null);
  const [field_errors, set_field_errors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!draft_q.isFetched) return;
    if (!draft_q.data) {
      navigate('/onboarding/start', { replace: true });
      return;
    }
    if (!draft_q.data.address_completed) {
      navigate('/onboarding/address', { replace: true });
      return;
    }
    if (!draft_q.data.module_types.includes('PHARMACY')) {
      navigate(next_module_step_after('PHARMACY', draft_q.data.module_types), {
        replace: true,
      });
    }
  }, [draft_q.isFetched, draft_q.data, navigate]);

  useEffect(() => {
    if (prefilled) return;
    if (saved_q.data) {
      set_drug_license_number(saved_q.data.drug_license_number);
      set_prefilled(true);
    }
  }, [saved_q.data, prefilled]);

  async function handle_submit(e: React.FormEvent) {
    e.preventDefault();
    set_error(null);

    const trimmed = drug_license_number.trim();
    if (trimmed.length < DRUG_LICENSE_MIN || trimmed.length > DRUG_LICENSE_MAX) {
      set_error(
        `Drug license number must be ${DRUG_LICENSE_MIN}–${DRUG_LICENSE_MAX} characters.`,
      );
      return;
    }
    if (!DRUG_LICENSE_REGEX.test(trimmed)) {
      set_error('Drug license may only contain letters, digits, dashes, slashes, dots, and spaces.');
      return;
    }

    try {
      await save_m.mutateAsync({ drug_license_number: trimmed });
      if (!draft_q.data) return;
      navigate(next_module_step_after('PHARMACY', draft_q.data.module_types));
    } catch (err) {
      set_field_errors(field_errors_from(err));
      set_error(
        err instanceof ApiError
          ? form_error_message(err, 'Could not save pharmacy details', { highlights_fields: true })
          : 'Could not save pharmacy details',
      );
    }
  }

  return (
    <form onSubmit={handle_submit}>
      <StepSection
        step_key="module-pharmacy"
        title="Pharmacy details"
        description="Drug license issued by your State Drug Controller. Upload the certificate file under Documents & photos."
        error={error}
        footer={
          <Button
            type="submit"
            variant="gradient"
            disabled={save_m.isPending}
            data-testid="module-pharmacy-submit"
          >
            {save_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save & continue
          </Button>
        }
      >
        <TextField
          label="Drug license number"
          name="drug_license_number"
          error={field_errors.drug_license_number}
          data-testid="field-pharmacy-drug-license"
          value={drug_license_number}
          onChange={(e) => set_drug_license_number(e.target.value)}
          required
          minLength={DRUG_LICENSE_MIN}
          maxLength={DRUG_LICENSE_MAX}
          placeholder="20B-MH/12345/2023"
          hint={`${DRUG_LICENSE_MIN}–${DRUG_LICENSE_MAX} chars · letters, digits, dashes, slashes, dots, spaces`}
        />
      </StepSection>
    </form>
  );
}
